import Vue from 'vue';
import VueRouter from 'vue-router';
import VueAppInsights from 'vue-application-insights';
import VueGtm from '@gtm-support/vue2-gtm';
import VueFacebookPixel from 'vue-analytics-facebook-pixel';
import VueMatomo from 'vue-matomo';

import policies from 'policies';
import { i18n } from '@/setup/i18n';
import routes from 'routes';
import store from 'store';
import NProgressApp from 'utils/nprogress';
import { UI_GET_MASTER_DATA, UI_SET_LOADER } from 'store/actions/ui';

Vue.use(VueRouter);

const base = window.location.pathname.startsWith('/contratacion') ? '/contratacion/' : '/';

const router = new VueRouter({
  mode: 'history',
  linkActiveClass: 'active',
  hashbang: false,
  base,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { x: 0, y: 0 };
  },
  routes
});

if (process.env.NODE_ENV === 'production') {
  Vue.use(VueAppInsights, {
    id: process.env.APP_INSIGHTS_INSTR_KEY,
    router
  });
  const domain = window.location.hostname;
  const domainGTMMap = JSON.parse(process.env.GOOGLE_TAG_MANAGER_BY_DOMAIN);

  const matchGTM = Object.keys(domainGTMMap).find(d => domain === d || domain.includes(d));
  const fallbackGtm = process.env.GOOGLE_TAG_MANAGER_ID;
  const appGoogleTagManagerId = matchGTM ? domainGTMMap[matchGTM] : fallbackGtm;

  if (appGoogleTagManagerId) {
    const appGoogleTagManagerIdParsed = appGoogleTagManagerId && appGoogleTagManagerId.split(',');
    Vue.use(VueGtm, {
      id: appGoogleTagManagerIdParsed,
      defer: false,
      enabled: true,
      loadScript: true,
      vueRouter: router,
      trackOnNextTick: false
    });
  }

  const appFacebookAppId = process.env.FACEBOOK_APP_ID;
  if (appFacebookAppId) {
    Vue.use(VueFacebookPixel);
    Vue.analytics.fbq.init(process.env.FACEBOOK_APP_ID);
  }

  if (process.env.APP_PIXEL_MATOMO === 'enabled') {
    Vue.use(VueMatomo, {
      host: process.env.APP_PIXEL_MATOMO_SITE_URL,
      siteId: process.env.APP_PIXEL_MATOMO_SITE_ID,
      trackerFileName: 'piwik',
      enableLinkTracking: true,
      trackInitialView: true,
      router
    });
  }
}
// Redirect to the error page
const redirectToErrorPage = (next, resource) =>
  next({
    name: 'error',
    params: { resource }
  });

router.beforeEach((to, from, next) => {
  const titleKey = to.meta.titleKey;
  const titleByKey = titleKey ? `| ${i18n.t(`title.${to.meta.titleKey}`)}` : '';
  document.title = `${process.env.APP_NAME} ${titleByKey}`;

  if (['error', '404', 'legal'].indexOf(to.name) > -1) return next();

  // Policies
  const metaStep = to.matched.some(m => m.meta.step !== null && !Number.isNaN(Number(m.meta.step)));
  return store
    .dispatch(`ui/${UI_GET_MASTER_DATA}`)
    .then(() => {
      if (metaStep) {
        policies.checkStep(to, from, next);
      } else {
        next();
      }
    })
    .catch(() => {
      // Redirect to the error page
      redirectToErrorPage(next, UI_GET_MASTER_DATA);
    });
});

// After navigation is confirmed, but before resolving...
router.beforeResolve((to, from, next) => {
  // If this isn't an initial page load.
  if (from.name) {
    // Start the route progress bar.
    NProgressApp.start();
  }
  next();
});

// When each route is finished evaluating...
// eslint-disable-next-line no-unused-vars
router.afterEach(to => {
  // Loader hidden
  store.dispatch(`ui/${UI_SET_LOADER}`, false);
  // Complete the animation of the route progress bar.
  NProgressApp.done();
});

export { router as default };
